/**
 * Dashboard My Network widget: classmates and staff with live presence.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

import { VAvatar, VInput, VText } from "form";
import { useAsterController } from "context";
import { actionNetworkList } from "context/action";
import { asArray, asObject, asString } from "utils/safeData";

/**
 * Compact contact list under My Group on the learner homepage.
 */
const DashboardNetwork = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [controller, dispatch] = useAsterController();
  const { networkContacts, loadedNetworkList, loadedDashboard, networkUnread } = controller;
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!controller.sessionHydrated) return;
    actionNetworkList(dispatch);
  }, [controller.sessionHydrated, dispatch]);

  const contacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return asArray(networkContacts)
      .filter((row) => {
        const item = asObject(row);
        const name = `${asString(item.fullname)} ${asString(item.username)}`.toLowerCase();
        return !term || name.includes(term);
      })
      .slice(0, 8);
  }, [networkContacts, search]);

  return (
    <>
      <Box className="w-full flex justify-center lg:justify-start pt-8">
        <VText className="text-2xl">{t("My Network")}</VText>
      </Box>
      <Box className="w-full rounded-md border border-solid border-gray-200 dark:border-gray-500 px-6 py-5 my-5">
        <VInput
          startIcon="search"
          placeholder={t("Search people")}
          size="small"
          className="v-input-noborder w-full mb-4"
          noBorder
          value={search}
          setValue={setSearch}
        />
        {loadedDashboard && loadedNetworkList ? (
          contacts.length ? (
            contacts.map((raw) => {
              const item = asObject(raw);
              const name = asString(item.fullname) || asString(item.username) || "-";
              const unread = Number(asObject(networkUnread)[String(item.user_id)]) || 0;
              return (
                <Box
                  key={item.user_id}
                  className="flex items-center gap-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md px-1"
                  onClick={() => navigate(`/network?peer=${item.user_id}`)}
                >
                  <Box className="relative">
                    <VAvatar size="sm" bgColor="light" src={asString(item.avatar)} />
                    {unread > 0 ? (
                      <VText
                        className="absolute -top-1 -right-1 px-1 h-4 bg-primary rounded-full flex justify-center items-center text-xs text-white z-10"
                        div
                        style={{ minWidth: "16px" }}
                      >
                        {unread}
                      </VText>
                    ) : null}
                    <Box
                      className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-800 ${item.online ? "bg-green-500" : "bg-gray-300"}`}
                    />
                  </Box>
                  <Box className="min-w-0">
                    <VText className="text-sm font-bold text-limit-1">{name}</VText>
                    <VText color="secondary" className="text-xs">
                      {item.online ? t("Online") : t("Offline")}
                    </VText>
                  </Box>
                </Box>
              );
            })
          ) : (
            <VText color="secondary" className="text-sm">
              {t("No contacts yet")}
            </VText>
          )
        ) : (
          <Skeleton className="w-full h-12" count={3} />
        )}
        <Link to="/network" className="no-underline">
          <VText color="secondary" className="text-sm mt-3 text-center">
            {t("See all")}
          </VText>
        </Link>
      </Box>
    </>
  );
};

export default DashboardNetwork;
